import { debounce } from "lodash";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { UserModel } from "../../context_providers/auth/user_model";
import { ToastType, useToast } from "../../context_providers/toast/toast_ctx";
import { GetEmps, GetEmployeesByDept } from "../../service/repos/users_repo";
import AutoCompleteField from "./common/autocomplete";

interface EmployeeSearchWidgetProps {
  onSelect: (data: UserModel) => void;
  onClear?: () => void;
  sub_dep?: string;
  department?: string;
  placeholder?: string;
  emp?: { uid: string; name: string };
  disabled?: boolean;
  withBtn?: boolean;
  required?: boolean;
  id?: string;
}

const EmployeeSearchWidget: FunctionComponent<EmployeeSearchWidgetProps> = ({
  onSelect,
  onClear,
  placeholder,
  emp,
  withBtn = true,
  disabled = false,
  sub_dep = "",
  department = "",
  id,
}) => {
  const { showToast } = useToast();
  const [search, setSearch] = useState("");
  const [emps, setEmps] = useState<UserModel[]>([]);
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);

  const subDepRef = useRef(sub_dep);
  const departmentRef = useRef(department);

  useEffect(() => {
    subDepRef.current = sub_dep;
  }, [sub_dep]);

  useEffect(() => {
    departmentRef.current = department;
  }, [department]);

  useEffect(() => {
    setSearch(emp?.name ?? "");
  }, [emp]);

  useEffect(() => {
    setEmps([]);
    if (edit && search && search !== (emp?.name ?? "")) {
      debouncedHandler(search);
    }
  }, [sub_dep, department]);

  useEffect(() => {
    if (search && edit) {
      if (emp && emp.name === search) return;
      debouncedHandler(search);
    }
    if (edit && search.trim() === "") {
      setEmps([]);
      onClear?.(); // ✅ notify parent
    }
  }, [search]);

  const searchEmp = async (query: string) => {
    setLoading(true);
    if (departmentRef.current.trim()) {
      const res = await GetEmployeesByDept(departmentRef.current.trim(), "");
      if (res.success) {
        const all: UserModel[] = res.data?.data ?? [];
        const filtered = all.filter((e) =>
          `${e.first_name} ${e.last_name}`
            .toLowerCase()
            .includes(query.toLowerCase())
        );
        setEmps(filtered);
      } else {
        setEmps([]);
        showToast({ type: ToastType.error, text: res.error ?? "" });
      }
    } else {
      const res = await GetEmps({
        search: query,
        all: true,
        active: true,
        sub_dep: subDepRef.current.trim() !== "" ? subDepRef.current : undefined,
      });
      if (res.success) {
        setEmps(res.data.data!);
      } else {
        setEmps([]);
        showToast({ type: ToastType.error, text: res.error ?? "" });
      }
    }
    setLoading(false);
  };

  const debouncedHandler = useCallback(
    debounce((query) => {
      if (query) searchEmp(query);
    }, 500),
    []
  );

  return (
    <div className="flex gap-1 items-center    w-full">
      {edit ? (
        <AutoCompleteField
          id={id}
          loading={loading}
          value={search}
          placeholder={placeholder ?? "Employees"}
          onSelect={(val) => {
            setEdit(false);
            setSearch(val.value.first_name + " " + val.value.last_name);
            onSelect(val.value);
          }}
          suggestions={emps.map((e) => ({
            label: (
              <div className="text-xs">
                <b> {e.first_name + " " + e.last_name} </b>
                <span
                  style={{ fontSize: "10px" }}
                >{` (${e.emp_profile?.department}-${e.emp_profile?.sub_dep})`}</span>
              </div>
            ),

            value: e,
          }))}
          onChange={(str) => {
            setSearch(str);
          }}
        />
      ) : (
        <div
          id={id}
          className="truncate text-xs px-2 py-2 focus:outline-none w-full border"
          onClick={() => {
            if (!disabled) {
              setEdit(true);
            }
          }}
        >
          {search || placeholder}
        </div>
      )}
    </div>
  );
};

export default EmployeeSearchWidget;
